/**
 * PLZ-zu-Ort und Ort-zu-PLZ Auto-Vervollstaendigung (DACH)
 *
 * Client-Side Lookup: JSON-Dateien werden per fetch() lazy geladen.
 * Unterstuetzt DACH + Liechtenstein + europaeische Nachbarlaender.
 *
 * Features:
 * - PLZ eingeben -> Ort wird automatisch befuellt
 * - Ort eingeben -> PLZ wird automatisch befuellt (bei kleinen Orten)
 * - Kombi-Validierung: PLZ und Ort muessen zusammenpassen
 * - WAI-ARIA Combobox fuer Screenreader
 *
 * @package Brandner_Custom
 * @since 2.1.0
 */
(function($) {
    'use strict';

    // PLZ-Datenbank Cache (pro Land)
    var plzCache = {};

    // Reverse-Index Cache: Ort -> PLZ-Liste (pro Land)
    var reverseCache = {};

    // Loading-Promises (verhindert doppelte fetches)
    var loadingPromises = {};

    // Aktiver State
    var plzDebounceTimer = null;
    var cityDebounceTimer = null;
    var selectedIndex = -1;
    var $plzField = null;
    var $cityField = null;
    var $countryField = null;
    var $suggestionsContainer = null;
    var $citySuggestionsContainer = null;
    var $plzSelectionContainer = null;
    var $srAnnounce = null;
    var $comboHint = null;
    var initialized = false;
    var suppressCityLookup = false; // Verhindert Loop bei Auto-Fill

    // PLZ-Laengen pro Land (DACH + Europa)
    var PLZ_LENGTHS = {
        DE: 5, AT: 4, CH: 4, LI: 4,
        FR: 5, IT: 5, NL: 4, BE: 4, LU: 4,
        PL: 5, CZ: 5, DK: 4
    };

    // Max. PLZ-Anzahl fuer Auto-Fill
    var AUTO_FILL_MAX_PLZ = 3;

    // Max. Vorschlaege in Dropdown
    var MAX_SUGGESTIONS = 8;

    // =========================================================================
    // Normalisierung (spiegelt PHP normalize_ort)
    // =========================================================================

    function normalizeOrt(ort) {
        ort = ort.toLowerCase().trim();
        var replacements = {
            'ae': 'ae', 'oe': 'oe', 'ue': 'ue',
            '\u00e4': 'ae', '\u00f6': 'oe', '\u00fc': 'ue', '\u00df': 'ss',
            '\u00e9': 'e', '\u00e8': 'e', '\u00ea': 'e', '\u00e1': 'a', '\u00e0': 'a',
            '\u00e2': 'a', '\u00f3': 'o', '\u00f2': 'o', '\u00f4': 'o', '\u00fa': 'u',
            '\u00f9': 'u', '\u00fb': 'u', '\u00ed': 'i', '\u00ec': 'i', '\u00ee': 'i'
        };
        for (var key in replacements) {
            if (key.length === 2) continue; // Skip ae/oe/ue — nicht ersetzen
            ort = ort.split(key).join(replacements[key]);
        }
        ort = ort.replace(/[-\/\(\)]/g, ' ');
        ort = ort.replace(/\s+/g, ' ');
        return ort.trim();
    }

    /**
     * Dedupliziert Ortsnamen nach Normalisierung
     */
    function deduplicateCities(cities) {
        var seen = {};
        var result = [];
        for (var i = 0; i < cities.length; i++) {
            var normalized = normalizeOrt(cities[i]);
            if (!seen[normalized]) {
                seen[normalized] = true;
                result.push(cities[i]);
            }
        }
        return result;
    }

    // =========================================================================
    // Daten laden (Lazy-Load per fetch)
    // =========================================================================

    function getDataUrl(country) {
        if (!window.brandnerPlzLookup || !window.brandnerPlzLookup.dataUrls) {
            return null;
        }
        return window.brandnerPlzLookup.dataUrls[country] || null;
    }

    function loadPlzData(country) {
        if (plzCache[country]) {
            return Promise.resolve(plzCache[country]);
        }

        if (loadingPromises[country]) {
            return loadingPromises[country];
        }

        var url = getDataUrl(country);
        if (!url) {
            return Promise.resolve(null);
        }

        loadingPromises[country] = fetch(url)
            .then(function(response) {
                if (!response.ok) throw new Error('HTTP ' + response.status);
                return response.json();
            })
            .then(function(data) {
                plzCache[country] = data;
                buildReverseIndex(country, data);
                delete loadingPromises[country];
                return data;
            })
            .catch(function() {
                delete loadingPromises[country];
                return null;
            });

        return loadingPromises[country];
    }

    // =========================================================================
    // Reverse-Index: Ort -> PLZ-Liste
    // =========================================================================

    function buildReverseIndex(country, data) {
        if (reverseCache[country]) return;

        var index = {};
        for (var plz in data) {
            if (!data.hasOwnProperty(plz)) continue;
            var cities = data[plz];
            for (var i = 0; i < cities.length; i++) {
                var normalized = normalizeOrt(cities[i]);
                if (!index[normalized]) {
                    index[normalized] = { name: cities[i], plzList: [] };
                }
                if (index[normalized].plzList.indexOf(plz) === -1) {
                    index[normalized].plzList.push(plz);
                }
            }
        }
        reverseCache[country] = index;
    }

    function getReverseIndex(country) {
        return reverseCache[country] || null;
    }

    // =========================================================================
    // PLZ -> Ort Lookup
    // =========================================================================

    function getCurrentCountry() {
        if (!$countryField || !$countryField.length) return 'DE';
        return ($countryField.val() || 'DE').toUpperCase();
    }

    function getExpectedLength(country) {
        return PLZ_LENGTHS[country] || null;
    }

    /**
     * Normalisiert PLZ-Eingabe: Entfernt Leerzeichen und Bindestriche.
     * PL: "00-001" -> "00001", CZ: "100 00" -> "10000"
     */
    function normalizePlzInput(plz) {
        return plz.replace(/[\s-]/g, '');
    }

    function doPlzLookup() {
        if (!$plzField || !$plzField.length) return;

        var country = getCurrentCountry();
        var expectedLen = getExpectedLength(country);

        // Land ohne PLZ-Daten
        if (!expectedLen) {
            clearPlzValidation();
            hideSuggestions('plz');
            return;
        }

        var plz = normalizePlzInput(($plzField.val() || '').trim());

        // PLZ noch nicht vollstaendig
        if (plz.length < expectedLen) {
            clearPlzValidation();
            hideSuggestions('plz');
            clearComboValidation();
            return;
        }

        // PLZ ungueltiges Format
        if (!/^\d+$/.test(plz) || plz.length !== expectedLen) {
            setPlzValidation(false);
            hideSuggestions('plz');
            return;
        }

        // Daten laden und Lookup durchfuehren
        loadPlzData(country).then(function(data) {
            if (!data) {
                clearPlzValidation();
                return;
            }

            var cities = data[plz];
            if (!cities || !cities.length) {
                setPlzValidation(false);
                hideSuggestions('plz');
                return;
            }

            setPlzValidation(true);
            var unique = deduplicateCities(cities);

            if (unique.length === 1) {
                // Ein Ort -> direkt einfuellen (nur wenn Feld nicht readonly)
                if ($cityField.prop('readonly')) return;
                suppressCityLookup = true;
                $cityField.val(unique[0]).trigger('change');
                suppressCityLookup = false;
                hideSuggestions('plz');
                validateCombo();
            } else {
                // Mehrere Orte -> Dropdown anzeigen
                showPlzSuggestions(unique);
            }
        });
    }

    // =========================================================================
    // Ort -> PLZ Reverse-Lookup
    // =========================================================================

    function doCityLookup() {
        if (!$cityField || !$cityField.length || suppressCityLookup) return;

        var country = getCurrentCountry();
        if (!PLZ_LENGTHS[country]) {
            hideSuggestions('city');
            return;
        }

        var input = ($cityField.val() || '').trim();
        if (input.length < 2) {
            hideSuggestions('city');
            return;
        }

        loadPlzData(country).then(function() {
            var index = getReverseIndex(country);
            if (!index) {
                hideSuggestions('city');
                return;
            }

            var normalizedInput = normalizeOrt(input);
            var matches = [];

            for (var key in index) {
                if (!index.hasOwnProperty(key)) continue;
                if (key.indexOf(normalizedInput) === 0) {
                    matches.push(index[key]);
                }
            }

            // Sortieren nach Name
            matches.sort(function(a, b) {
                return a.name.localeCompare(b.name);
            });

            // Exakter Match? -> Nicht anzeigen
            if (matches.length === 1 && normalizeOrt(matches[0].name) === normalizedInput) {
                hideSuggestions('city');
                autoFillPlz(matches[0]);
                return;
            }

            if (matches.length === 0) {
                hideSuggestions('city');
                return;
            }

            // Limitieren
            if (matches.length > MAX_SUGGESTIONS) {
                matches = matches.slice(0, MAX_SUGGESTIONS);
            }

            showCitySuggestions(matches);
        });
    }

    function autoFillPlz(match) {
        if (!match || !match.plzList || !match.plzList.length) return;
        if (!$plzField || !$plzField.length) return;
        if ($plzField.prop('readonly')) return;

        // Nur auto-fill wenn PLZ-Feld leer oder PLZ nicht zum Ort passt
        var currentPlz = ($plzField.val() || '').trim();
        if (currentPlz && match.plzList.indexOf(currentPlz) !== -1) {
            // PLZ passt schon zum Ort
            validateCombo();
            return;
        }

        if (match.plzList.length <= AUTO_FILL_MAX_PLZ) {
            // Kleine Gemeinde -> erste PLZ setzen
            $plzField.val(match.plzList[0]).trigger('change');
            $plzField.addClass('brandner-plz-autofilled');
            setTimeout(function() {
                $plzField.removeClass('brandner-plz-autofilled');
            }, 2000);
            setPlzValidation(true);
            validateCombo();
        } else {
            // Grosse Stadt -> PLZ-Auswahl-Dropdown anzeigen
            showPlzSelectionDropdown(match.plzList);
        }
    }

    // =========================================================================
    // Visuelles Feedback
    // =========================================================================

    function setPlzValidation(valid) {
        if (!$plzField) return;
        $plzField.removeClass('brandner-plz-valid brandner-plz-invalid');
        $plzField.addClass(valid ? 'brandner-plz-valid' : 'brandner-plz-invalid');
    }

    function clearPlzValidation() {
        if (!$plzField) return;
        $plzField.removeClass('brandner-plz-valid brandner-plz-invalid');
    }

    function setCityValidation(state) {
        if (!$cityField) return;
        $cityField.removeClass('brandner-city-valid brandner-city-invalid brandner-city-warning');
        if (state === 'valid') {
            $cityField.addClass('brandner-city-valid');
        } else if (state === 'warning') {
            $cityField.addClass('brandner-city-warning');
        } else if (state === 'invalid') {
            $cityField.addClass('brandner-city-invalid');
        }
    }

    function clearCityValidation() {
        if (!$cityField) return;
        $cityField.removeClass('brandner-city-valid brandner-city-invalid brandner-city-warning');
    }

    // =========================================================================
    // Kombi-Validierung (Phase 3)
    // =========================================================================

    function validateCombo() {
        if (!$plzField || !$cityField) return;

        var country = getCurrentCountry();
        if (!PLZ_LENGTHS[country]) {
            clearComboValidation();
            return;
        }

        var plz = normalizePlzInput(($plzField.val() || '').trim());
        var city = ($cityField.val() || '').trim();

        // Nur validieren wenn beide Felder ausgefuellt
        if (!plz || !city) {
            clearComboValidation();
            return;
        }

        var expectedLen = getExpectedLength(country);
        if (plz.length !== expectedLen || !/^\d+$/.test(plz)) {
            clearComboValidation();
            return;
        }

        loadPlzData(country).then(function(data) {
            if (!data) {
                clearComboValidation();
                return;
            }

            var cities = data[plz];
            if (!cities || !cities.length) {
                // PLZ existiert nicht
                setPlzValidation(false);
                clearCityValidation();
                hideComboHint();
                return;
            }

            // Pruefen ob der Ort zur PLZ passt
            var normalizedCity = normalizeOrt(city);
            var match = false;
            for (var i = 0; i < cities.length; i++) {
                if (normalizeOrt(cities[i]) === normalizedCity) {
                    match = true;
                    break;
                }
            }

            if (match) {
                setPlzValidation(true);
                setCityValidation('valid');
                hideComboHint();
            } else {
                // Mismatch — Warnung anzeigen (nicht blockierend)
                setPlzValidation(true); // PLZ existiert
                setCityValidation('invalid');
                showComboHint('PLZ ' + plz + ' geh\u00f6rt zu: ' + deduplicateCities(cities).join(', '));
            }
        });
    }

    function clearComboValidation() {
        clearCityValidation();
        hideComboHint();
    }

    function showComboHint(message) {
        if (!$comboHint) {
            $comboHint = $('<div>', {
                'class': 'brandner-plz-combo-hint',
                'role': 'alert'
            });
            var $wrapper = $cityField.closest('.form-row, .woocommerce-input-wrapper, .brandner-form-group, p');
            if ($wrapper.length) {
                $wrapper.append($comboHint);
            } else {
                $cityField.after($comboHint);
            }
        }
        $comboHint.text(message).show();
    }

    function hideComboHint() {
        if ($comboHint) {
            $comboHint.hide();
        }
    }

    // =========================================================================
    // PLZ-Vorschlagsliste (WAI-ARIA Combobox)
    // =========================================================================

    function createSuggestionsContainer(type) {
        if (type === 'city') {
            if ($citySuggestionsContainer && $citySuggestionsContainer.length) return $citySuggestionsContainer;

            $citySuggestionsContainer = $('<ul>', {
                'id': 'city-suggestions',
                'class': 'brandner-plz-suggestions brandner-city-suggestions',
                'role': 'listbox',
                'aria-label': 'Ortsvorschl\u00e4ge'
            }).hide();

            var $wrapper = $cityField.closest('.form-row, .woocommerce-input-wrapper, .brandner-form-group, p');
            if ($wrapper.length) {
                $wrapper.css('position', 'relative');
                $wrapper.append($citySuggestionsContainer);
            } else {
                $cityField.after($citySuggestionsContainer);
            }
            return $citySuggestionsContainer;
        }

        // PLZ suggestions (original)
        if ($suggestionsContainer && $suggestionsContainer.length) return $suggestionsContainer;

        $suggestionsContainer = $('<ul>', {
            'id': 'plz-suggestions',
            'class': 'brandner-plz-suggestions',
            'role': 'listbox',
            'aria-label': 'Ortsvorschl\u00e4ge'
        }).hide();

        $srAnnounce = $('<div>', {
            'class': 'brandner-plz-sr-only',
            'aria-live': 'polite',
            'aria-atomic': 'true'
        });

        // Container nach dem City-Feld einfuegen
        var $wrapper = $cityField.closest('.form-row, .woocommerce-input-wrapper, .brandner-form-group, p');
        if ($wrapper.length) {
            $wrapper.css('position', 'relative');
            $wrapper.append($suggestionsContainer);
            $wrapper.append($srAnnounce);
        } else {
            $cityField.after($suggestionsContainer).after($srAnnounce);
        }
        return $suggestionsContainer;
    }

    function showPlzSuggestions(cities) {
        hideSuggestions('city'); // Andere schliessen
        createSuggestionsContainer('plz');

        selectedIndex = -1;
        $suggestionsContainer.empty();

        for (var i = 0; i < cities.length; i++) {
            var $item = $('<li>', {
                'id': 'plz-option-' + i,
                'class': 'brandner-plz-suggestion-item',
                'role': 'option',
                'aria-selected': 'false',
                'text': cities[i]
            });
            $item.data('city', cities[i]);
            $suggestionsContainer.append($item);
        }

        $suggestionsContainer.show();

        // ARIA-Attribute am City-Feld
        $cityField.attr({
            'aria-expanded': 'true',
            'aria-controls': 'plz-suggestions',
            'role': 'combobox',
            'aria-autocomplete': 'list'
        });

        // Screenreader-Ankuendigung
        if ($srAnnounce) {
            $srAnnounce.text(cities.length + ' Ortsvorschl\u00e4ge gefunden');
        }

        // Click-Handler fuer Eintraege
        $suggestionsContainer.off('click.plzLookup').on('click.plzLookup', '.brandner-plz-suggestion-item', function() {
            selectCityFromPlz($(this).data('city'));
        });

        bindClickOutside();
    }

    function showCitySuggestions(matches) {
        hideSuggestions('plz'); // Andere schliessen
        createSuggestionsContainer('city');

        selectedIndex = -1;
        $citySuggestionsContainer.empty();

        for (var i = 0; i < matches.length; i++) {
            var plzInfo = matches[i].plzList.length <= 3
                ? ' (' + matches[i].plzList.join(', ') + ')'
                : ' (' + matches[i].plzList.length + ' PLZ)';

            var $item = $('<li>', {
                'id': 'city-option-' + i,
                'class': 'brandner-plz-suggestion-item',
                'role': 'option',
                'aria-selected': 'false'
            });
            $item.append($('<span>', { 'class': 'brandner-suggestion-name', text: matches[i].name }));
            $item.append($('<span>', { 'class': 'brandner-suggestion-plz', text: plzInfo }));
            $item.data('match', matches[i]);
            $citySuggestionsContainer.append($item);
        }

        $citySuggestionsContainer.show();

        $cityField.attr({
            'aria-expanded': 'true',
            'aria-controls': 'city-suggestions',
            'role': 'combobox',
            'aria-autocomplete': 'list'
        });

        if ($srAnnounce) {
            $srAnnounce.text(matches.length + ' Ortsvorschl\u00e4ge gefunden');
        }

        // Click-Handler fuer Eintraege
        $citySuggestionsContainer.off('click.plzLookup').on('click.plzLookup', '.brandner-plz-suggestion-item', function() {
            var match = $(this).data('match');
            selectCityFromReverse(match);
        });

        bindClickOutside();
    }

    // =========================================================================
    // PLZ-Auswahl-Dropdown (bei Staedten mit vielen PLZ)
    // =========================================================================

    function showPlzSelectionDropdown(plzList) {
        hideSuggestions(); // Alle anderen Dropdowns schliessen

        // PLZ sortieren
        plzList = plzList.slice().sort();

        // Container erstellen falls noetig
        if (!$plzSelectionContainer || !$plzSelectionContainer.length) {
            $plzSelectionContainer = $('<ul>', {
                'id': 'plz-selection',
                'class': 'brandner-plz-suggestions brandner-plz-selection',
                'role': 'listbox',
                'aria-label': 'PLZ ausw\u00e4hlen'
            }).hide();

            var $wrapper = $plzField.closest('.form-row, .woocommerce-input-wrapper, .brandner-form-group, p');
            if ($wrapper.length) {
                $wrapper.css('position', 'relative');
                $wrapper.append($plzSelectionContainer);
            } else {
                $plzField.after($plzSelectionContainer);
            }
        }

        selectedIndex = -1;
        $plzSelectionContainer.empty();

        for (var i = 0; i < plzList.length; i++) {
            var $item = $('<li>', {
                'id': 'plz-select-option-' + i,
                'class': 'brandner-plz-suggestion-item',
                'role': 'option',
                'aria-selected': 'false',
                'text': plzList[i]
            });
            $item.data('plz', plzList[i]);
            $plzSelectionContainer.append($item);
        }

        $plzSelectionContainer.show();

        // ARIA-Attribute am PLZ-Feld
        $plzField.attr({
            'aria-expanded': 'true',
            'aria-controls': 'plz-selection',
            'role': 'combobox',
            'aria-autocomplete': 'list'
        });

        // Screenreader-Ankuendigung
        if ($srAnnounce) {
            $srAnnounce.text(plzList.length + ' Postleitzahlen zur Auswahl');
        }

        // Click-Handler
        $plzSelectionContainer.off('click.plzLookup').on('click.plzLookup', '.brandner-plz-suggestion-item', function() {
            selectPlzFromDropdown($(this).data('plz'));
        });

        // Delay damit der aktuelle Click-Event (City-Suggestion) nicht sofort
        // den Click-Outside-Handler triggert
        setTimeout(function() {
            bindClickOutside();
        }, 0);
    }

    function selectPlzFromDropdown(plz) {
        $plzField.val(plz).trigger('change');
        $plzField.addClass('brandner-plz-autofilled');
        setTimeout(function() {
            $plzField.removeClass('brandner-plz-autofilled');
        }, 2000);
        setPlzValidation(true);
        hideSuggestions();
        validateCombo();
        $cityField.focus();
    }

    function hideSuggestions(type) {
        if (type === 'city' || !type) {
            if ($citySuggestionsContainer) {
                $citySuggestionsContainer.hide().empty();
            }
        }
        if (type === 'plz' || !type) {
            if ($suggestionsContainer) {
                $suggestionsContainer.hide().empty();
            }
        }
        if (type === 'plzSelect' || !type) {
            if ($plzSelectionContainer) {
                $plzSelectionContainer.hide().empty();
            }
        }

        selectedIndex = -1;

        if ($cityField && $cityField.length) {
            var plzVisible = $suggestionsContainer && $suggestionsContainer.is(':visible');
            var cityVisible = $citySuggestionsContainer && $citySuggestionsContainer.is(':visible');
            if (!plzVisible && !cityVisible) {
                $cityField.removeAttr('aria-expanded aria-controls aria-activedescendant')
                          .removeAttr('role aria-autocomplete');
            }
        }

        if ($plzField && $plzField.length) {
            var plzSelectVisible = $plzSelectionContainer && $plzSelectionContainer.is(':visible');
            if (!plzSelectVisible) {
                $plzField.removeAttr('aria-expanded aria-controls aria-activedescendant')
                         .removeAttr('role aria-autocomplete');
            }
        }

        if (type === undefined) {
            $(document).off('click.plzLookupOutside');
        }
    }

    function bindClickOutside() {
        $(document).off('click.plzLookupOutside').on('click.plzLookupOutside', function(e) {
            if (!$(e.target).closest('.brandner-plz-suggestions, .brandner-city-suggestions, .brandner-plz-selection, #billing_city, #billing_postcode').length) {
                hideSuggestions();
            }
        });
    }

    function selectCityFromPlz(city) {
        if ($cityField.prop('readonly')) return;
        suppressCityLookup = true;
        $cityField.val(city).trigger('change');
        suppressCityLookup = false;
        hideSuggestions();
        $cityField.focus();
        validateCombo();
    }

    function selectCityFromReverse(match) {
        suppressCityLookup = true;
        $cityField.val(match.name).trigger('change');
        suppressCityLookup = false;
        hideSuggestions();
        autoFillPlz(match);
        $plzField.focus();
    }

    function getActiveContainer() {
        if ($plzSelectionContainer && $plzSelectionContainer.is(':visible')) {
            return $plzSelectionContainer;
        }
        if ($citySuggestionsContainer && $citySuggestionsContainer.is(':visible')) {
            return $citySuggestionsContainer;
        }
        if ($suggestionsContainer && $suggestionsContainer.is(':visible')) {
            return $suggestionsContainer;
        }
        return null;
    }

    function navigateSuggestions(direction) {
        var $container = getActiveContainer();
        if (!$container) return;

        var $items = $container.find('.brandner-plz-suggestion-item');
        if (!$items.length) return;

        // Alten Eintrag deselektieren
        if (selectedIndex >= 0) {
            $items.eq(selectedIndex).attr('aria-selected', 'false');
        }

        // Neuen Index berechnen
        if (direction === 'down') {
            selectedIndex = (selectedIndex + 1) % $items.length;
        } else {
            selectedIndex = selectedIndex <= 0 ? $items.length - 1 : selectedIndex - 1;
        }

        // Neuen Eintrag markieren
        var $active = $items.eq(selectedIndex);
        $active.attr('aria-selected', 'true');
        var $ariaField = ($container === $plzSelectionContainer) ? $plzField : $cityField;
        $ariaField.attr('aria-activedescendant', $active.attr('id'));

        // Scroll in Sicht
        var container = $container[0];
        var item = $active[0];
        if (item.offsetTop < container.scrollTop) {
            container.scrollTop = item.offsetTop;
        } else if (item.offsetTop + item.offsetHeight > container.scrollTop + container.clientHeight) {
            container.scrollTop = item.offsetTop + item.offsetHeight - container.clientHeight;
        }
    }

    // =========================================================================
    // Event-Handler
    // =========================================================================

    function onPlzInput() {
        clearTimeout(plzDebounceTimer);
        plzDebounceTimer = setTimeout(doPlzLookup, 200);
    }

    function onCityInput() {
        if (suppressCityLookup) return;
        clearTimeout(cityDebounceTimer);
        cityDebounceTimer = setTimeout(doCityLookup, 250);
    }

    function onCityBlur() {
        // Kleiner Delay damit Click auf Suggestion noch funktioniert
        setTimeout(function() {
            if (!$cityField) return;
            var $container = getActiveContainer();
            if ($container && $container.is(':visible')) return; // Noch offen

            var country = getCurrentCountry();
            if (!PLZ_LENGTHS[country]) return;

            var city = ($cityField.val() || '').trim();
            if (!city) {
                clearCityValidation();
                return;
            }

            var index = getReverseIndex(country);
            if (!index) return;

            var normalizedCity = normalizeOrt(city);
            if (index[normalizedCity]) {
                setCityValidation('valid');
            } else {
                setCityValidation('warning');
            }

            // Kombi-Validierung nach Blur
            validateCombo();
        }, 200);
    }

    function onPlzBlur() {
        setTimeout(function() {
            validateCombo();
        }, 200);
    }

    function onCountryChange() {
        var country = getCurrentCountry();

        clearPlzValidation();
        clearCityValidation();
        clearComboValidation();
        hideSuggestions();

        // Preload Daten fuer neues Land
        if (PLZ_LENGTHS[country]) {
            loadPlzData(country);
            // Re-Lookup mit bestehendem PLZ-Wert
            clearTimeout(plzDebounceTimer);
            plzDebounceTimer = setTimeout(doPlzLookup, 100);
        }
    }

    function onPlzKeydown(e) {
        // Nur aktiv wenn PLZ-Auswahl-Dropdown offen
        if (!$plzSelectionContainer || !$plzSelectionContainer.is(':visible')) return;

        switch (e.keyCode) {
            case 40: // Arrow Down
                e.preventDefault();
                navigateSuggestions('down');
                break;
            case 38: // Arrow Up
                e.preventDefault();
                navigateSuggestions('up');
                break;
            case 13: // Enter
                if (selectedIndex >= 0) {
                    e.preventDefault();
                    var $items = $plzSelectionContainer.find('.brandner-plz-suggestion-item');
                    var $selected = $items.eq(selectedIndex);
                    if ($selected.length) {
                        selectPlzFromDropdown($selected.data('plz'));
                    }
                }
                break;
            case 27: // Escape
                e.preventDefault();
                hideSuggestions('plzSelect');
                break;
        }
    }

    function onCityKeydown(e) {
        var $container = getActiveContainer();
        if (!$container) return;

        switch (e.keyCode) {
            case 40: // Arrow Down
                e.preventDefault();
                navigateSuggestions('down');
                break;
            case 38: // Arrow Up
                e.preventDefault();
                navigateSuggestions('up');
                break;
            case 13: // Enter
                if (selectedIndex >= 0) {
                    e.preventDefault();
                    var $items = $container.find('.brandner-plz-suggestion-item');
                    var $selected = $items.eq(selectedIndex);
                    if ($selected.length) {
                        // Unterscheiden ob PLZ- oder City-Suggestions
                        if ($container.hasClass('brandner-city-suggestions')) {
                            selectCityFromReverse($selected.data('match'));
                        } else {
                            selectCityFromPlz($selected.data('city'));
                        }
                    }
                }
                break;
            case 27: // Escape
                e.preventDefault();
                hideSuggestions();
                break;
        }
    }

    // =========================================================================
    // Init / Destroy
    // =========================================================================

    function init() {
        if (initialized) destroy();

        $plzField = $('#billing_postcode');
        $cityField = $('#billing_city');
        $countryField = $('#billing_country');

        if (!$plzField.length || !$cityField.length) return;

        // Event-Handler binden
        $plzField.off('input.plzLookup').on('input.plzLookup', onPlzInput);
        $plzField.off('blur.plzLookup').on('blur.plzLookup', onPlzBlur);
        $plzField.off('keydown.plzLookup').on('keydown.plzLookup', onPlzKeydown);
        $cityField.off('input.plzLookup').on('input.plzLookup', onCityInput);
        $cityField.off('blur.plzLookup').on('blur.plzLookup', onCityBlur);
        $cityField.off('keydown.plzLookup').on('keydown.plzLookup', onCityKeydown);
        $countryField.off('change.plzLookup').on('change.plzLookup', onCountryChange);

        // Preload Daten fuer aktuelles Land
        var country = getCurrentCountry();
        if (PLZ_LENGTHS[country]) {
            loadPlzData(country);
        }

        initialized = true;
    }

    function destroy() {
        clearTimeout(plzDebounceTimer);
        clearTimeout(cityDebounceTimer);
        hideSuggestions();
        clearPlzValidation();
        clearCityValidation();
        clearComboValidation();

        if ($plzField) $plzField.off('.plzLookup');
        if ($cityField) $cityField.off('.plzLookup');
        if ($countryField) $countryField.off('.plzLookup');

        if ($suggestionsContainer) {
            $suggestionsContainer.remove();
            $suggestionsContainer = null;
        }
        if ($citySuggestionsContainer) {
            $citySuggestionsContainer.remove();
            $citySuggestionsContainer = null;
        }
        if ($plzSelectionContainer) {
            $plzSelectionContainer.remove();
            $plzSelectionContainer = null;
        }
        if ($srAnnounce) {
            $srAnnounce.remove();
            $srAnnounce = null;
        }
        if ($comboHint) {
            $comboHint.remove();
            $comboHint = null;
        }

        $(document).off('click.plzLookupOutside');

        $plzField = null;
        $cityField = null;
        $countryField = null;
        initialized = false;
    }

    // Expose for PJAX
    window.BrandnerPlzLookupInit = init;
    window.BrandnerPlzLookupDestroy = destroy;

    $(function() {
        init();
    });

})(jQuery);
